// Helpers di formattazione data/ora italiani (gg/mm/aaaa, 24h)
// Centralizzati qui per essere riutilizzati ovunque nel prodotto.
//
// Note importanti:
// - `<input type="date">` e `<input type="time">` mostrano il formato
//   determinato dal LOCALE DEL BROWSER, non da CSS. Su molti browser
//   italiani il formato è già gg/mm/aaaa; ma su alcuni mobile (Chrome
//   con locale en-US) può essere MM/DD/YYYY. Per garantire visualizzazione
//   coerente, le date che mostriamo come TESTO usano questi helper.
// - L'input HTML `value` ISO (YYYY-MM-DD) resta lo standard verso il backend.

const PAD = (n) => String(n).padStart(2, "0");

/**
 * Formatta una stringa ISO (YYYY-MM-DD oppure ISO datetime) come gg/mm/aaaa.
 * Esempio: "2026-06-30" → "30/06/2026"
 */
export const fmtDateIT = (iso) => {
  if (!iso) return "";
  const datePart = String(iso).slice(0, 10);
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    // Fallback: prova Date.parse
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return `${PAD(d.getDate())}/${PAD(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/**
 * Formatta una stringa ISO completa come gg/mm/aaaa HH:MM (24h).
 */
export const fmtDateTimeIT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${PAD(d.getDate())}/${PAD(d.getMonth() + 1)}/${d.getFullYear()} ${PAD(d.getHours())}:${PAD(d.getMinutes())}`;
};

/**
 * Formatta una stringa ora "HH:MM" o ISO datetime in 24h "HH:MM".
 */
export const fmtTimeIT = (val) => {
  if (!val) return "";
  // Già "HH:MM" o "HH:MM:SS"
  const m = String(val).match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${PAD(m[1])}:${m[2]}`;
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return `${PAD(d.getHours())}:${PAD(d.getMinutes())}`;
};

/**
 * Formatta un range date come gg/mm/aaaa → gg/mm/aaaa.
 */
export const fmtDateRangeIT = (from, to) => {
  const a = fmtDateIT(from);
  const b = fmtDateIT(to);
  if (a && b) return a === b ? a : `${a} → ${b}`;
  return a || b || "";
};

/**
 * Restituisce la rappresentazione "relativa" italiana (ora, 3 min fa, 2 g fa, …)
 * per timestamp ISO recenti. Per > 7 giorni torna a formato completo.
 */
export const fmtRelativeIT = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ora";
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} g fa`;
  return fmtDateTimeIT(iso);
};

/**
 * Per il display "header" di un mese tipo "Giugno 2026".
 */
const MESI_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
export const fmtMonthYearIT = (year, monthIdx1Based) => {
  const idx = Number(monthIdx1Based) - 1;
  if (idx < 0 || idx > 11) return "";
  return `${MESI_IT[idx]} ${year}`;
};

/**
 * Restituisce il valore ISO odierno (YYYY-MM-DD) nel fuso locale del browser.
 * Utile come `min` su <input type="date">.
 */
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
};
